import React, { useContext, useState } from "react";
import { useEffectOnce } from "../../../utils/effects";
import { Box, Grid, Typography, TextField, MenuItem, Button, Chip, Stack, Backdrop, CircularProgress } from "@mui/material";
import DataTable from "../../dataTable/DataTable";
import EditIcon from "@mui/icons-material/Edit";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { useDispatch, useSelector } from "react-redux";
import AuthContext from "../../../context/AuthContext";
import { getGlobalExchanges, updateGlobalExchange } from "./GlobalExchanges.thunk";
import ExchangeEditForm from "./ExchangeEditForm";


const currencyStyles = {
  USD: { color: "#2E86C1", label: "USD" },
  MXN: { color: "#964DE3", label: "MXN" },
  COP: { color: "#F1C40F", label: "COP" },
  PEN: { color: "#884EA0", label: "PEN" },
  BRL: { color: "#C240B3", label: "BRL" },
  CLP: { color: "#E67E22", label: "CLP" },
  ARS: { color: "#3498DB", label: "ARS" },
  BOB: { color: "#D35400", label: "BOB" },
  VES: { color: "#1ABC9C", label: "VES" },
};

const categoryOptions = [
  { value: "", label: "Select" },
  { value: "rtps", label: "RTPS" },
  { value: "exchanges", label: "Exchanges" },
  { value: "stablecoins", label: "Stablecoins" },
];

const statusOptions = [
  { value: "", label: "Select" },
  { value: "E", label: "Enabled" },
  { value: "D", label: "Disabled" },
];

const renderExchangeIcon = (value, row) => (
  <Box display="flex" alignItems="center" justifyContent="center">
    {value ? <img src={value} alt={row.name} style={{ width: 28 }} /> : <CurrencyExchangeIcon />}
  </Box>
);

const renderCurrencies = (currencies) => (
  <Stack direction="row" spacing={1}>
    {Array.isArray(currencies) && currencies.map((cur) => (
      <Chip
        key={cur}
        label={currencyStyles[cur]?.label || cur}
        sx={{ background: currencyStyles[cur]?.color || 'grey.500', color: 'common.white', fontWeight: 'bold' }}
        size="small"
      />
    ))}
  </Stack>
);

const GlobalExchanges = () => {
  const dispatch = useDispatch();
  const { token, acquirerData } = useContext(AuthContext);
  const { exchanges, status, statusUpdate, page, pageSize, totalRecords } = useSelector((s) => s.globalExchanges || {});
  const isSaving = statusUpdate === "loading";

  const [filterStatus, setFilterStatus] = useState("");
  const [filterCode, setFilterCode] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [editingExchange, setEditingExchange] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffectOnce(() => {
    dispatch(getGlobalExchanges({
      token,
      acquirerID: acquirerData.acquirerId,
      acquirerName: acquirerData.acquirerName,
      status: filterStatus,
      code: filterCode,
      category: filterCategory,
      page,
      pageSize,
    }));
  });

  const handleFilter = () => {
    dispatch(getGlobalExchanges({
      token,
      acquirerID: acquirerData.acquirerId,
      acquirerName: acquirerData.acquirerName,
      status: filterStatus,
      code: filterCode,
      category: filterCategory,
      page: 1,
      pageSize,
    }));
  };

  const columns = [
    {
      id: "icon",
      label: "",
      minWidth: 60,
      format: renderExchangeIcon,
    },
    { id: "code", label: "Code" },
    { id: "name", label: "Exchange" },
    { id: "category", label: "Category" },
    { id: "position", label: "Position" },
    {
      id: "currencies",
      label: "Currencies",
      format: renderCurrencies,
    },
    { id: "active", label: "Status", format: (v) => (v ? "Enabled" : "Disabled") },
    {
      id: "action",
      label: "Actions",
      actions: [
        {
          icon: <EditIcon />, label: "Edit",
          onClick: (row) => {
            setEditingExchange(row);
            setIsEditing(true);
          },
        },
      ],
    },
  ];

  const handlePageChange = (event, newPage) => {
    dispatch(
      getGlobalExchanges({
        token,
        acquirerID: acquirerData.acquirerId,
        acquirerName: acquirerData.acquirerName,
        status: filterStatus,
        code: filterCode,
        category: filterCategory,
        page: newPage + 1,
        pageSize,
      })
    );
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = +event.target.value;
    dispatch(
      getGlobalExchanges({
        token,
        acquirerID: acquirerData.acquirerId,
        acquirerName: acquirerData.acquirerName,
        status: filterStatus,
        code: filterCode,
        category: filterCategory,
        page: 1,
        pageSize: newPageSize,
      })
    );
  };

  const handleSave = async (updatedExchange) => {
    if (isSaving) return;
    try {
      await dispatch(updateGlobalExchange({
        token,
        exchangeCode: updatedExchange.code,
        exchangeCatalogId: updatedExchange.catalogId,
        exchangeRowKey: updatedExchange.id,
        exchangeStatus: updatedExchange.active ? "E" : "D",
        position: updatedExchange.position,
        currencies: updatedExchange.currencies,
        kycIdentityEnabled: updatedExchange.kyc_identity_enabled,
        kycProviderCode: updatedExchange.kyc_provider_code,
        kycProviderAccountKey: updatedExchange.kyc_provider_account_key,
        kycIdentityThresholdUsd: updatedExchange.kyc_identity_threshold_usd,
        amlEnabled: updatedExchange.aml,
        acquirerId: acquirerData.acquirerId,
        acquirerName: acquirerData.acquirerName,
      })).unwrap();

      dispatch(getGlobalExchanges({
        token,
        acquirerID: acquirerData.acquirerId,
        acquirerName: acquirerData.acquirerName,
        status: filterStatus,
        code: filterCode,
        category: filterCategory,
        page,
        pageSize,
      }));

      setIsEditing(false);
      setEditingExchange(null);
    } catch (err) {
      console.error("Update exchange failed", err);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    setIsEditing(false);
    setEditingExchange(null);
  };

  if (isEditing && editingExchange) {
    return (
      <Box>
        <Backdrop open={isSaving} sx={{ color: "common.white", zIndex: 1301 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h6">Exchange Edit</Typography>
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
              Back
            </Button>
          </Grid>
        </Grid>
        <ExchangeEditForm
          exchange={editingExchange}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h6">Global Exchanges</Typography>
        </Grid>
      </Grid>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Code"
          value={filterCode}
          onChange={e => setFilterCode(e.target.value)}
          size="small"
        />
        <TextField
          label="Category"
          select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        >
          {categoryOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Status"
          select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        >
          {statusOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" color="primary" onClick={handleFilter}>
          Filter
        </Button>
      </Box>
      <DataTable
        loading={status === "loading"}
        columns={columns}
        rows={exchanges}
        page={page - 1}
        rowsPerPage={pageSize}
        count={totalRecords}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        showPagination={true}
      />
    </Box>
  );
};

export default GlobalExchanges;
